// Sprint 0082 — Sanctioned Strength Master page.
// Define approved headcount per dept/section/shift/role. Drives manpower
// gap analysis. PENDING → APPROVED workflow.

import { useState } from 'react'
import {
  App, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Popconfirm,
  Select, Space, Statistic, Tag, Tooltip, Typography,
} from 'antd'
import {
  CheckOutlined, EditOutlined, PlusOutlined, StopOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  sanctionedApi, WORKER_CATEGORIES, MACHINE_TYPES,
  type SanctionedStrength,
} from '@/features/hr/manpowerApi'
import { designationsApi } from '@/features/hr/enablementApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { shiftsApi } from '@/features/masters/shifts/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import MachineGroupsCard from './MachineGroupsCard'
import DepartmentFloatingRolesCard from './DepartmentFloatingRolesCard'

const { Text } = Typography

export default function SanctionedStrengthPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const { plantId } = usePlantId()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()
  const [gapDept, setGapDept] = useState<string | undefined>()
  const [gapDate, setGapDate] = useState<string>(dayjs().format('YYYY-MM-DD'))

  const { data: depts } = useQuery({
    queryKey: ['departments-all', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200, plant_id: plantId }),
  })
  const { data: designations } = useQuery({
    queryKey: ['hr-designations'],
    queryFn: () => designationsApi.list(),
  })
  const { data: rows, isLoading } = useQuery({
    queryKey: ['sanctioned', plantId],
    queryFn: () => sanctionedApi.list({ plant_id: plantId }),
    enabled: !!plantId,
  })
  // Shifts for the create/edit form's optional Shift picker. Show all
  // (including inactive) so users can sanction headcount for night shifts
  // that aren't activated yet — common pattern when planning for ramp-up.
  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts-for-sanctioned', plantId],
    queryFn: () => shiftsApi.list({ plant_id: plantId }),
    enabled: !!plantId,
  })
  const shiftMap = Object.fromEntries(shifts.map((s) => [s.id, s]))
  const shiftOptions = shifts.map((s) => ({
    label: `${s.shift_code} — ${s.shift_name}${s.is_active ? '' : ' (inactive)'}`,
    value: s.id,
  }))
  const { data: gap, refetch: refetchGap } = useQuery({
    queryKey: ['gap', plantId, gapDept, gapDate],
    queryFn: () => sanctionedApi.gapAnalysis({
      plant_id: plantId!, department_id: gapDept!, as_of: gapDate,
    }),
    enabled: !!(plantId && gapDept),
  })

  const deptName = (id: string) =>
    depts?.items.find((d) => d.id === id)?.name ?? '—'

  const saveMutation = useMutation({
    mutationFn: (vals: Record<string, unknown>) =>
      sanctionedApi.create({
        plant_id: plantId!,
        department_id: vals.department_id as string,
        section_code: (vals.section_code as string) || null,
        machine_type: (vals.machine_type as string) || null,
        designation_code: (vals.designation_code as string) || null,
        worker_category: vals.worker_category as string,
        // shift_id is OPTIONAL — floor roles usually pick a shift,
        // office/staff roles often don't need one. Empty = applies
        // across all shifts (or no shift split).
        shift_id: (vals.shift_id as string) || null,
        sanctioned_count: vals.sanctioned_count as number,
        effective_from: (vals.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
        effective_to: vals.effective_to
          ? (vals.effective_to as dayjs.Dayjs).format('YYYY-MM-DD') : null,
        remarks: (vals.remarks as string) || null,
      }),
    onSuccess: () => {
      message.success('Sanctioned strength created — pending approval')
      setDrawerOpen(false)
      qc.invalidateQueries({ queryKey: ['sanctioned'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => sanctionedApi.approve(id),
    onSuccess: () => {
      message.success('Approved')
      qc.invalidateQueries({ queryKey: ['sanctioned'] })
    },
  })

  const [editing, setEditing] = useState<SanctionedStrength | null>(null)
  const [editForm] = Form.useForm()

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<SanctionedStrength> }) =>
      sanctionedApi.patch(id, body),
    onSuccess: () => {
      message.success('Updated')
      qc.invalidateQueries({ queryKey: ['sanctioned'] })
      setEditing(null)
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Update failed')
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => sanctionedApi.deactivate(id),
    onSuccess: () => {
      message.success('Row deactivated — kept for audit, removed from gap analysis')
      qc.invalidateQueries({ queryKey: ['sanctioned'] })
    },
  })

  const endDateMutation = useMutation({
    mutationFn: ({ id, on_date }: { id: string; on_date: string }) =>
      sanctionedApi.endDate(id, on_date),
    onSuccess: () => {
      message.success('Row end-dated')
      qc.invalidateQueries({ queryKey: ['sanctioned'] })
    },
  })

  const columns = [
    {
      title: 'Department', key: 'department_id',
      render: (_: unknown, r: SanctionedStrength) => deptName(r.department_id),
    },
    { title: 'Section', dataIndex: 'section_code', key: 'section_code', render: (v: string | null) => v ?? '—' },
    { title: 'Designation', dataIndex: 'designation_code', key: 'designation_code', render: (v: string | null) => v ?? '—' },
    {
      title: 'Category', dataIndex: 'worker_category', key: 'worker_category',
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Shift', dataIndex: 'shift_id', key: 'shift_id', width: 90,
      render: (v: string | null) => {
        if (!v) return <Text type="secondary" style={{ fontSize: 12 }}>any</Text>
        const s = shiftMap[v]
        if (!s) return <Tag color="red">missing</Tag>
        return (
          <Tooltip title={`${s.shift_name} (${s.start_time}–${s.end_time})`}>
            <Tag color={s.is_active ? 'blue' : 'default'}>{s.shift_code}</Tag>
          </Tooltip>
        )
      },
    },
    { title: 'Count', dataIndex: 'sanctioned_count', key: 'sanctioned_count' },
    {
      title: 'Effective', key: 'effective',
      render: (_: unknown, r: SanctionedStrength) =>
        `${dayjs(r.effective_from).format('DD MMM YY')} → ${r.effective_to ? dayjs(r.effective_to).format('DD MMM YY') : '—'}`,
    },
    {
      title: 'Status', dataIndex: 'approval_status', key: 'approval_status',
      render: (v: string) =>
        <Tag color={v === 'APPROVED' ? 'green' : v === 'REJECTED' ? 'red' : 'gold'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 220,
      render: (_: unknown, r: SanctionedStrength) => {
        if (!canEdit) return null
        return (
          <Space size={4} wrap>
            {r.approval_status === 'PENDING' && (
              <Tooltip title="Approve this sanctioned row (becomes active in gap analysis)">
                <Button size="small" type="primary" icon={<CheckOutlined />}
                  onClick={() => approveMutation.mutate(r.id)}>
                  Approve
                </Button>
              </Tooltip>
            )}
            <Tooltip title={r.approval_status === 'APPROVED'
              ? 'Edit only sanctioned_count, remarks, or end-date — other fields need a new row'
              : 'Edit any field (this row is still PENDING)'}>
              <Button size="small" icon={<EditOutlined />}
                onClick={() => {
                  editForm.setFieldsValue({
                    sanctioned_count: r.sanctioned_count,
                    worker_category: r.worker_category,
                    shift_id: r.shift_id,
                    designation_code: r.designation_code,
                    section_code: r.section_code,
                    effective_from: r.effective_from ? dayjs(r.effective_from) : undefined,
                    effective_to: r.effective_to ? dayjs(r.effective_to) : undefined,
                    remarks: r.remarks,
                  })
                  setEditing(r)
                }}>
                Edit
              </Button>
            </Tooltip>
            <Popconfirm
              title="Deactivate this row?"
              description={
                <span style={{ fontSize: 12, maxWidth: 280, display: 'block' }}>
                  Row stays in DB for audit, but stops appearing in gap analysis.
                  Use for typos or wrong entries. For policy changes, use End-date instead.
                </span>
              }
              okText="Deactivate"
              okButtonProps={{ danger: true }}
              onConfirm={() => deactivateMutation.mutate(r.id)}
            >
              <Tooltip title="Soft-delete (keeps audit history)">
                <Button size="small" danger icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Sanctioned Strength"
        subtitle="Approved headcount baseline by department × section × role × category. Drives manpower gap analysis."
        extra={canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            form.resetFields(); setDrawerOpen(true)
          }}>New Sanction</Button>
        )}
      />

      {/* Gap analysis card */}
      <Card size="small" title="Manpower Gap Analysis" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select allowClear placeholder="Department" style={{ width: 220 }}
            options={(depts?.items ?? []).map((d) => ({ label: d.name, value: d.id }))}
            onChange={setGapDept} />
          <DatePicker value={dayjs(gapDate)}
            onChange={(d) => setGapDate(d?.format('YYYY-MM-DD') ?? dayjs().format('YYYY-MM-DD'))} />
          <Button onClick={() => refetchGap()}>Recompute</Button>
        </Space>
        {gap && gap.breakdown.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Space wrap size={16}>
              {gap.breakdown.map((b) => (
                <Card key={b.worker_category} size="small" style={{ minWidth: 180 }}>
                  <Statistic
                    title={b.worker_category}
                    value={b.actual}
                    suffix={`/ ${b.sanctioned}`}
                    valueStyle={{
                      color: b.shortage > 0 ? '#cf1322' :
                        b.excess > 0 ? '#fa8c16' : '#52c41a',
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {b.shortage > 0 ? `Short ${b.shortage}` :
                      b.excess > 0 ? `Excess ${b.excess}` : 'OK'}
                  </Text>
                </Card>
              ))}
            </Space>
          </div>
        )}
      </Card>

      <DataTable rowKey="id" columns={columns} dataSource={rows ?? []} loading={isLoading} />

      {/* HR-004 follow-up — Machine Groups admin. 1 operator covers N
          machines. Mill admin configures groups for non-Ring-Frame
          sections (Cards, Drawing, Combing, etc.) */}
      <MachineGroupsCard />

      {/* HR-004 follow-up — Department Floating Roles. Configure roving
          roles each dept needs per shift (Gaiter, Jobber, Sweeper,
          Reliever, etc.) */}
      <DepartmentFloatingRolesCard />

      <Modal
        title="New Sanctioned Strength"
        open={drawerOpen}
        onCancel={() => setDrawerOpen(false)}
        onOk={() => form.validateFields().then((v) => saveMutation.mutate(v))}
        confirmLoading={saveMutation.isPending}
        width={520}
      >
        <Form form={form} layout="vertical" initialValues={{ worker_category: 'PERMANENT' }}>
          <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
            <Select options={(depts?.items ?? []).map((d) => ({ label: d.name, value: d.id }))} />
          </Form.Item>
          <Form.Item name="section_code" label="Section (free text, optional)">
            <Input placeholder="e.g. Cards A-line" />
          </Form.Item>
          <Form.Item name="machine_type" label="Machine Type (optional)">
            <Select allowClear options={MACHINE_TYPES.map((m) => ({ label: m, value: m }))} />
          </Form.Item>
          <Form.Item name="designation_code" label="Designation (optional)">
            <Select allowClear showSearch
              options={(designations ?? []).map((d) => ({
                label: `${d.designation_name} (${d.designation_code})`,
                value: d.designation_code,
              }))} />
          </Form.Item>
          <Form.Item name="worker_category" label="Worker Category" rules={[{ required: true }]}>
            <Select options={WORKER_CATEGORIES.map((c) => ({ label: c, value: c }))} />
          </Form.Item>
          {/* Shift is OPTIONAL — floor roles split by shift (e.g. "2 Cleaners
              on night, 1 on day"), but staff/office roles often don't. Leave
              blank to mean "applies across all shifts / no shift split". */}
          <Form.Item
            name="shift_id"
            label="Shift (optional)"
            tooltip="Pick a shift if this headcount applies to a specific shift only. Leave blank for staff/office roles that don't split by shift."
          >
            <Select allowClear options={shiftOptions} placeholder="No shift split" />
          </Form.Item>
          <Form.Item name="sanctioned_count" label="Sanctioned Count" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_from" label="Effective From" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_to" label="Effective Till (optional)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit modal ─────────────────────────────────────────────── */}
      <Modal
        title={editing ? `Edit row — ${editing.approval_status}` : 'Edit'}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={() => editForm.submit()}
        confirmLoading={patchMutation.isPending}
        width={520}
      >
        {editing && (
          <>
            {editing.approval_status === 'APPROVED' && (
              <div style={{
                marginBottom: 12, padding: 8, background: '#fffbe6',
                border: '1px solid #ffe58f', borderRadius: 4, fontSize: 12,
              }}>
                ⚠️ This row is <b>APPROVED</b>. You can only edit
                <b> Sanctioned Count</b>, <b>Remarks</b>, and <b>Effective Till</b>.
                To change other fields, deactivate this row and create a new one.
              </div>
            )}
            <Form
              form={editForm}
              layout="vertical"
              onFinish={(values) => {
                const isApproved = editing.approval_status === 'APPROVED'
                const body: Partial<SanctionedStrength> = {
                  sanctioned_count: values.sanctioned_count,
                  remarks: values.remarks ?? null,
                  effective_to: values.effective_to
                    ? dayjs(values.effective_to).format('YYYY-MM-DD')
                    : null,
                }
                if (!isApproved) {
                  body.worker_category = values.worker_category
                  body.shift_id = values.shift_id
                  body.designation_code = values.designation_code ?? null
                  body.section_code = values.section_code ?? null
                  body.effective_from = values.effective_from
                    ? dayjs(values.effective_from).format('YYYY-MM-DD')
                    : undefined
                }
                patchMutation.mutate({ id: editing.id, body })
              }}
            >
              {editing.approval_status !== 'APPROVED' && (
                <>
                  <Form.Item name="designation_code" label="Designation">
                    <Select allowClear showSearch
                      options={(designations ?? []).map((d) => ({
                        label: `${d.designation_name} (${d.designation_code})`,
                        value: d.designation_code,
                      }))} />
                  </Form.Item>
                  <Form.Item name="worker_category" label="Category">
                    <Select options={WORKER_CATEGORIES.map((c) => ({ label: c, value: c }))} />
                  </Form.Item>
                  <Form.Item
                    name="shift_id"
                    label="Shift (optional)"
                    tooltip="Pick a shift if this headcount applies to a specific shift only. Leave blank for staff/office roles that don't split by shift."
                  >
                    <Select allowClear options={shiftOptions} placeholder="No shift split" />
                  </Form.Item>
                  <Form.Item name="effective_from" label="Effective From">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </>
              )}
              <Form.Item name="sanctioned_count" label="Sanctioned Count" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="effective_to" label="Effective Till (leave blank = open-ended)">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="remarks" label="Remarks">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  )
}
